/**
 * The small parts every dashboard tab and the report share: the info pop-up, the
 * Measured / Estimate badge, the shadow-mode line, the status line, a figure card with its
 * loading, error and "as of" states, the change line and the slow-load notice.
 *
 * Colour rules (design 2.3): green, amber and red only say status; a count or a share is
 * never coloured because it is "good news". Information (shadow mode, estimate, a switch
 * that is on) is `info`. A change is always written out with a sign and an arrow, so
 * colour is never the only carrier.
 */

import { ArrowDown, ArrowUp, Icon, Info, Minus, RefreshCw } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  cn,
  Freshness,
  Popover,
  Skeleton,
  Typography,
} from "@norican/react";
import { type ReactNode, useEffect, useState } from "react";

import type { ExecutiveShare } from "@/lib/api";
import { type Delta, formatClock, formatPercent, formatXofY } from "@/lib/format";
import { useTrackOnce } from "@/lib/usage-react";

import { DEFINITIONS, LABELS, SLOW_NOTICE, TEST_RUN_LINE, TOO_FEW } from "./copy";

/** A small "i" that explains a figure; opens on hover, focus or tap. */
export function InfoTip({ label, text }: { label: string; text: ReactNode }) {
  return (
    <Popover.Root>
      <Popover.Trigger
        openOnHover
        delay={150}
        aria-label={`About ${label}`}
        className="text-fg-subtle hover:text-fg inline-flex shrink-0 cursor-help items-center align-middle print:hidden"
      >
        <Icon icon={Info} size="xs" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="top" sideOffset={6}>
          <Popover.Popup className="max-w-xs">
            <Popover.Body>
              <Typography variant="body-sm">{text}</Typography>
            </Popover.Body>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

export type FigureKind = "measured" | "estimate";

/** "Measured" (neutral) or "Estimate" (information). */
export function KindBadge({ kind = "measured" }: { kind?: FigureKind }) {
  return (
    <Badge
      tone={kind === "estimate" ? "info" : "neutral"}
      variant="outline"
      size="sm"
      title={kind === "estimate" ? DEFINITIONS.estimate : DEFINITIONS.measured}
    >
      {kind === "estimate" ? LABELS.estimate : LABELS.measured}
    </Badge>
  );
}

/** The fixed shadow-mode line under every page title. */
export function TestRunLine({ className }: { className?: string }) {
  return (
    <span className={cn("inline-flex items-start gap-2", className)}>
      <Badge tone="info" variant="subtle" size="sm" className="mt-0.5 shrink-0">
        Shadow mode
      </Badge>
      <span>{TEST_RUN_LINE.replace(/^Shadow mode: /, "")}</span>
    </span>
  );
}

/** A run that finished longer ago than this is late (the import runs every 5 minutes). */
export const IMPORT_STALE_MS = 45 * 60_000;

/** What the status line says, from `/metrics/executive` or `/metrics/ops`. */
export interface StatusFacts {
  running: "ok" | "degraded" | "stale";
  lastImportAt: string | null;
  importStalled: boolean;
  masterDataOk: boolean;
  sendBlocked: boolean;
}

/** One row of four dots: running, mail import age, master data, nothing sent. */
export function StatusLine({ facts, now }: { facts: StatusFacts; now?: number }) {
  const runningTone =
    facts.importStalled || facts.running === "stale"
      ? "negative"
      : facts.running === "degraded"
        ? "caution"
        : "positive";
  return (
    <Card variant="outline" padding="sm" aria-label={LABELS.status}>
      <div className="grid grid-cols-2 items-center gap-x-6 gap-y-2 text-sm sm:flex sm:flex-wrap">
        <Freshness
          tone={runningTone}
          label={
            runningTone === "positive"
              ? "Running"
              : runningTone === "caution"
                ? "Running, some steps late"
                : "Stopped"
          }
        />
        {facts.lastImportAt ? (
          <Freshness
            label="Mail import"
            since={facts.lastImportAt}
            staleAfter={IMPORT_STALE_MS}
            tone={facts.importStalled ? "negative" : undefined}
            now={now}
          />
        ) : (
          <Freshness label="No mail import yet" tone="caution" />
        )}
        <Freshness
          tone={facts.masterDataOk ? "positive" : "caution"}
          label={facts.masterDataOk ? "Master data current" : "Master data late"}
        />
        <Freshness
          tone={facts.sendBlocked ? "info" : "caution"}
          label={facts.sendBlocked ? "Nothing sent (shadow mode)" : "Send block off"}
        />
        <span className="col-span-2 sm:ml-auto">
          <InfoTip label={LABELS.status} text={DEFINITIONS.status} />
        </span>
      </div>
    </Card>
  );
}

/** "As of 08:40" when the figures are older than 15 minutes, else nothing. */
export function AsOf({ computedAt, now }: { computedAt?: string | null; now?: number }) {
  if (!computedAt) {
    return null;
  }
  const at = new Date(computedAt).getTime();
  if (Number.isNaN(at) || (now ?? Date.now()) - at < 15 * 60_000) {
    return null;
  }
  return (
    <Typography as="span" variant="caption" tone="muted">
      As of {formatClock(computedAt)}
    </Typography>
  );
}

/** True once `active` has lasted longer than `ms` (for the slow first-load notice). */
export function useSlow(active: boolean, ms = 3000): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!active) {
      setSlow(false);
      return;
    }
    const timer = window.setTimeout(() => setSlow(true), ms);
    return () => window.clearTimeout(timer);
  }, [active, ms]);
  return slow;
}

export function SlowNotice({ show }: { show: boolean }) {
  if (!show) {
    return null;
  }
  return (
    <Typography variant="caption" tone="muted" role="status">
      {SLOW_NOTICE}
    </Typography>
  );
}

/** The error of one card: the rest of the page stays. */
export function CardError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const status = (error as { status?: number } | null)?.status;
  // The usage log: a person saw a card that did not load. The status only, no text.
  useTrackOnce("error.page", { status, shown: "card" }, "shown");
  const text =
    status === 403
      ? "Your role does not show these figures."
      : status === 503
        ? "The figures are not available now."
        : "I2Q could not load these figures.";
  return (
    <Alert tone="danger" className="items-center">
      <span className="flex flex-wrap items-center gap-2">
        {text}
        {onRetry && status !== 403 && (
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            onClick={onRetry}
            startIcon={<Icon icon={RefreshCw} size="xs" />}
          >
            Retry
          </Button>
        )}
      </span>
    </Alert>
  );
}

export interface FigureCardProps {
  title: string;
  /** The definition in the info pop-up. */
  info?: ReactNode;
  kind?: FigureKind;
  /** The days the figures cover ("7 Sep to 4 Oct"), shown beside the title. */
  period?: string;
  /** Right of the title (a toggle, a link). */
  actions?: ReactNode;
  loading?: boolean;
  /** Dim the content while newer figures are on their way. */
  refreshing?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** The height the content will have, kept while loading so nothing jumps. */
  skeletonHeight?: number;
  footer?: ReactNode;
  className?: string;
  bodyClassName?: string;
  id?: string;
  children?: ReactNode;
}

/** The days a card's figures cover, in the card header. */
export function PeriodCaption({ text }: { text: string }) {
  return (
    <Typography as="span" variant="caption" tone="subtle" data-period="">
      {text}
    </Typography>
  );
}

/** A titled card with the info pop-up, the Measured badge and the loading and error states. */
export function FigureCard({
  title,
  info,
  kind,
  period,
  actions,
  loading,
  refreshing,
  error,
  onRetry,
  skeletonHeight = 120,
  footer,
  className,
  bodyClassName,
  id,
  children,
}: FigureCardProps) {
  return (
    <Card variant="outline" padding="none" className={cn("min-w-0", className)} id={id}>
      <Card.Header className="flex flex-wrap items-center gap-2">
        <Typography as="h2" variant="label" className="text-fg">
          {title}
        </Typography>
        {info && <InfoTip label={title} text={info} />}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {period && <PeriodCaption text={period} />}
          {actions}
          {kind && <KindBadge kind={kind} />}
        </span>
      </Card.Header>
      <Card.Body
        className={cn(
          "flex flex-col gap-3 transition-opacity",
          refreshing && "opacity-60",
          bodyClassName,
        )}
        aria-busy={loading || refreshing || undefined}
      >
        {loading ? (
          <Skeleton variant="rect" height={skeletonHeight} />
        ) : error ? (
          <CardError error={error} onRetry={onRetry} />
        ) : (
          children
        )}
      </Card.Body>
      {footer && !loading && !error ? <Card.Footer>{footer}</Card.Footer> : null}
    </Card>
  );
}

/** Tone of a change: only quality figures get one, and only for a real move on enough data. */
export function deltaTone(
  delta: Delta | null,
  { quality, enough }: { quality: boolean; enough: boolean },
): "success" | "warning" | "neutral" {
  if (!delta || !quality || !enough || Math.abs(delta.size) < 2) {
    return "neutral";
  }
  return delta.direction === "up" ? "success" : delta.direction === "down" ? "warning" : "neutral";
}

const TONE_TEXT = {
  success: "text-success-fg",
  warning: "text-warning-fg",
  neutral: "text-fg-muted",
} as const;

/** "▲ +3 pts vs previous 4 weeks", the arrow and the sign carry the direction. */
export function DeltaText({
  delta,
  against,
  tone = "neutral",
}: {
  delta: Delta | null;
  against: string;
  tone?: "success" | "warning" | "neutral";
}) {
  if (!delta) {
    return null;
  }
  const icon = delta.direction === "up" ? ArrowUp : delta.direction === "down" ? ArrowDown : Minus;
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs font-semibold", TONE_TEXT[tone])}>
      <Icon icon={icon} size="xs" />
      <span className="tabular-nums">{delta.text}</span>
      <span className="text-fg-muted font-normal">{against}</span>
    </span>
  );
}

/** A share as the page prints it: "54%", or "14 of 17" when the base is too small. */
export interface ShareText {
  value: string;
  /** False when the base is too small: print muted, with {@link TOO_FEW}. */
  enough: boolean;
}

export function shareText(
  share: ExecutiveShare | null | undefined,
  options: { precise?: boolean } = {},
): ShareText | null {
  if (!share || share.base <= 0) {
    return null;
  }
  if (!share.enough || share.share == null) {
    return { value: formatXofY(share.count, share.base), enough: false };
  }
  return {
    value: formatPercent(share.share, options) ?? formatXofY(share.count, share.base),
    enough: true,
  };
}

/** A figure in a table cell or a line: the share, or "x of y" muted, or the fallback muted. */
export function ShareCell({
  share,
  precise,
  fallback,
  className,
}: {
  share: ExecutiveShare | null | undefined;
  precise?: boolean;
  fallback: string;
  className?: string;
}) {
  const text = shareText(share, { precise });
  if (!text) {
    return <span className={cn("text-fg-subtle text-xs", className)}>{fallback}</span>;
  }
  return (
    <span
      className={cn("tabular-nums", !text.enough && "text-fg-muted", className)}
      title={text.enough ? undefined : TOO_FEW}
    >
      {text.value}
    </span>
  );
}
